/**
 * Enum of all order states as stings.
 *
 * @type {Readonly<{RESUMED: string, DISCARDED: string, UNSENT: string, SERVICE_STARTED: string, RECEIVED: string, PICKUP_ARRIVAL: string, COMPLETED: string, ACCEPTED: string, PICKUP_BEGIN: string, DELIVERY_STARTED: string, DELIVERY_ARRIVAL: string, REJECTED: string, SERVICE_ARRIVAL: string, SERVICE_COMPLETED: string, PICKUP_STARTED: string, PICKUP_COMPLETED: string, READ: string, SERVICE_DEPARTED: string, CANCELLED: string, SERVICE_BEGIN: string, SUSPENDED: string, DELIVERY_START: string, DELIVERY_DEPARTED: string, DELIVERY_COMPLETED: string, SENT: string, PICKUP_DEPARTED: string}>}
 */
const OrderStates = Object.freeze({
    ACCEPTED: 'ACCEPTED',
    CANCELLED: 'CANCELLED',
    COMPLETED: 'COMPLETED',
    DELIVERY_ARRIVAL: 'DELIVERY_ARRIVAL',
    DELIVERY_COMPLETED: 'DELIVERY_COMPLETED',
    DELIVERY_DEPARTED: 'DELIVERY_DEPARTED',
    DELIVERY_START: 'DELIVERY_START',
    DELIVERY_STARTED: 'DELIVERY_STARTED',
    DISCARDED: 'DISCARDED',
    PICKUP_ARRIVAL: 'PICKUP_ARRIVAL',
    PICKUP_BEGIN: 'PICKUP_BEGIN',
    PICKUP_COMPLETED: 'PICKUP_COMPLETED',
    PICKUP_DEPARTED: 'PICKUP_DEPARTED',
    PICKUP_STARTED: 'PICKUP_STARTED',
    READ: 'READ',
    RECEIVED: 'RECEIVED',
    REJECTED: 'REJECTED',
    RESUMED: 'RESUMED',
    SENT: 'SENT',
    SERVICE_ARRIVAL: 'SERVICE_ARRIVAL',
    SERVICE_BEGIN: 'SERVICE_BEGIN',
    SERVICE_COMPLETED: 'SERVICE_COMPLETED',
    SERVICE_DEPARTED: 'SERVICE_DEPARTED',
    SERVICE_STARTED: 'SERVICE_STARTED',
    SUSPENDED: 'SUSPENDED',
    UNSENT: 'UNSENT'
})

/**
 * Order states which are considered as active, because they represent steps in the work on the actual order.
 * @type {Readonly<{[p: string]: string}>}
 */
const ActiveOrderStates = Object.freeze(
    Object.fromEntries(
        [
            OrderStates.SERVICE_STARTED,
            OrderStates.SERVICE_ARRIVAL,
            OrderStates.SERVICE_BEGIN,
            OrderStates.PICKUP_STARTED,
            OrderStates.PICKUP_ARRIVAL,
            OrderStates.PICKUP_BEGIN,
            OrderStates.DELIVERY_STARTED,
            OrderStates.DELIVERY_ARRIVAL,
            OrderStates.DELIVERY_START
        ].map(state => [state, state])
    )
)

/**
 * Order states from which an order can be deleted.
 * @type {Readonly<{[p: string]: string}>}
 */
const DeletableOrderStates = Object.freeze(
    Object.fromEntries(
        [
            OrderStates.UNSENT,
            OrderStates.COMPLETED,
            OrderStates.CANCELLED,
            OrderStates.REJECTED,
            OrderStates.DISCARDED
        ].map(state => [state, state])
    )
)

/**
 * Order states from which an order can be sent.
 * @type {Readonly<{[p: string]: string}>}
 */
const SendableOrderStates = Object.freeze(
    Object.fromEntries([OrderStates.UNSENT, OrderStates.CANCELLED, OrderStates.REJECTED].map(state => [state, state]))
)

/**
 * Order states from which an order can be canceled. UNSENT, CANCELLED, REJECTED, COMPLETED, DISCARDED are excluded.
 * @type {Readonly<{[p: string]: string}>}
 */
const CancelableOrderStates = Object.freeze(
    Object.fromEntries(
        [
            OrderStates.ACCEPTED,
            OrderStates.DELIVERY_ARRIVAL,
            OrderStates.DELIVERY_COMPLETED,
            OrderStates.DELIVERY_DEPARTED,
            OrderStates.DELIVERY_START,
            OrderStates.DELIVERY_STARTED,
            OrderStates.PICKUP_ARRIVAL,
            OrderStates.PICKUP_BEGIN,
            OrderStates.PICKUP_COMPLETED,
            OrderStates.PICKUP_DEPARTED,
            OrderStates.PICKUP_STARTED,
            OrderStates.READ,
            OrderStates.RECEIVED,
            OrderStates.RESUMED,
            OrderStates.SENT,
            OrderStates.SERVICE_ARRIVAL,
            OrderStates.SERVICE_BEGIN,
            OrderStates.SERVICE_COMPLETED,
            OrderStates.SERVICE_DEPARTED,
            OrderStates.SERVICE_STARTED,
            OrderStates.SUSPENDED
        ].map(state => [state, state])
    )
)

const OrderStatesSubsets = Object.freeze({
    Active: ActiveOrderStates,
    Deletable: DeletableOrderStates,
    Sendable: SendableOrderStates,
    Cancelable: CancelableOrderStates
})

export { OrderStates, OrderStatesSubsets }
